import React, {useState} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {useForm} from 'react-hook-form'
import {useBranding} from '../../hooks/useBranding'
import {useFileUpload} from '../../hooks/useFileUpload'
import {useAnalytics} from '../../hooks/useAnalytics'
import AdminBreadcrumb from './AdminBreadcrumb'
import SEOHead from '../SEOHead'
import SafeIcon from '../../common/SafeIcon'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import * as FiIcons from 'react-icons/fi'

const {FiUpload, FiX, FiSave, FiPlus, FiTrash2, FiEye, FiRefreshCw, FiPalette} = FiIcons

const BrandingSettings = () => {
  const {branding, loading, updateBranding} = useBranding()
  const {uploadFile, uploading} = useFileUpload()
  const {trackCTAClick} = useAnalytics()
  const [saving, setSaving] = useState(false)
  const [logoPreview, setLogoPreview] = useState('')
  const [faviconPreview, setFaviconPreview] = useState('')
  const [footerLinks, setFooterLinks] = useState([])

  const {register, handleSubmit, setValue, watch, formState: {errors}} = useForm({
    defaultValues: {
      primary_color: '#399696',
      secondary_color: '#a0cfbc',
      accent_color: '#ffe8ad',
      heading_font: 'Inter',
      body_font: 'Inter'
    }
  })

  // Empower Nutrition Palette
  const empowerPalette = [
    {name: 'Dark Gray', color: '#272727', description: 'Primary text and headers'},
    {name: 'Soft Mint Green', color: '#a0cfbc', description: 'Calm, nurturing accent'},
    {name: 'Warm Yellow', color: '#ffe8ad', description: 'Energy and positivity'},
    {name: 'Teal', color: '#399696', description: 'Trust and stability'},
    {name: 'Pure White', color: '#fefefe', description: 'Clean backgrounds'},
    {name: 'Light Gray', color: '#b8bac1', description: 'Subtle text and borders'},
    {name: 'Aqua Mint', color: '#77cfbc', description: 'Fresh, healthy vibes'},
    {name: 'True Black', color: '#000000', description: 'High contrast text'},
    {name: 'Bright Pink', color: '#FF66C4', description: 'Call-to-action accent'}
  ]

  React.useEffect(() => {
    if (branding) {
      setValue('primary_color', branding.primary_color)
      setValue('secondary_color', branding.secondary_color)
      setValue('accent_color', branding.accent_color)
      setValue('heading_font', branding.heading_font)
      setValue('body_font', branding.body_font)
      setLogoPreview(branding.logo_url || '')
      setFaviconPreview(branding.favicon_url || '')
      setFooterLinks(branding.footer_links || [])
    }
  }, [branding, setValue])

  const handleFileChange = async (event, type) => {
    const file = event.target.files[0]
    if (!file) return

    try {
      const path = `${type}/${Date.now()}-${file.name}`
      const uploadedFile = await uploadFile(file, path, 'branding-assets')
      const fileUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/branding-assets/${uploadedFile.path}`

      if (type === 'logo') {
        setLogoPreview(fileUrl)
        trackCTAClick('logo_uploaded', 'branding_settings')
      } else if (type === 'favicon') {
        setFaviconPreview(fileUrl)
        trackCTAClick('favicon_uploaded', 'branding_settings')
      }

      toast.success(`${type === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully`)
    } catch (error) {
      toast.error(`Failed to upload ${type}`)
    }
  }

  const addFooterLink = () => {
    setFooterLinks(prev => [...prev, {label: '', url: ''}])
    trackCTAClick('footer_link_added', 'branding_settings')
  }

  const updateFooterLink = (index, field, value) => {
    setFooterLinks(prev => 
      prev.map((link, i) => 
        i === index ? {...link, [field]: value} : link
      )
    )
  }

  const removeFooterLink = (index) => {
    setFooterLinks(prev => prev.filter((_, i) => i !== index))
    trackCTAClick('footer_link_removed', 'branding_settings')
  }

  const setColorFromPalette = (colorType, color) => {
    setValue(colorType, color)
    trackCTAClick(`palette_color_selected_${colorType}`, 'branding_settings')
    toast.success(`${colorType.replace('_', ' ')} updated`)
  }

  const onSubmit = async (data) => {
    setSaving(true)

    const brandingData = {
      ...data,
      logo_url: logoPreview || null,
      favicon_url: faviconPreview || null,
      footer_links: footerLinks.filter(link => link.label && link.url)
    }

    const {error} = await updateBranding(brandingData)
    
    if (!error) {
      trackCTAClick('branding_saved', 'branding_settings')
      toast.success('Branding updated successfully!')
    }
    
    setSaving(false)
  }

  const previewChanges = () => {
    const formData = watch()
    const previewBranding = {
      ...formData,
      logo_url: logoPreview,
      favicon_url: faviconPreview,
      footer_links: footerLinks.filter(link => link.label && link.url)
    }

    // Apply preview temporarily
    const {applyBrandingToDOM} = require('../../hooks/useBranding')
    applyBrandingToDOM(previewBranding)
    
    trackCTAClick('branding_previewed', 'branding_settings')
    toast.success('Preview applied! Refresh to revert.')
  }

  const breadcrumbItems = [
    {label: 'Admin', href: '/admin'},
    {label: 'Branding Settings', href: '#'}
  ]

  const fontOptions = [
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 
    'Poppins', 'Source Sans Pro', 'Raleway', 'PT Sans', 'Nunito'
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Branding Settings | Admin | Empower Nutrition"
        description="Customize your site's appearance, colors, fonts, and branding"
        url="/admin/branding"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminBreadcrumb items={breadcrumbItems} />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Branding Settings</h1>
          <p className="text-gray-600 mt-2">
            Customize your site's appearance, colors, fonts, and branding
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Logo & Favicon Section */}
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-6">Logo & Favicon</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo
                </label>
                <div className="space-y-4">
                  {logoPreview && (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-16 w-auto border border-gray-200 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => setLogoPreview('')}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <SafeIcon icon={FiX} className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-2 pb-3">
                        <SafeIcon icon={FiUpload} className="w-6 h-6 mb-2 text-gray-500" />
                        <p className="text-sm text-gray-500">Upload logo</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'logo')}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Favicon Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Favicon
                </label>
                <div className="space-y-4">
                  {faviconPreview && (
                    <div className="relative">
                      <img
                        src={faviconPreview}
                        alt="Favicon preview"
                        className="h-8 w-8 border border-gray-200 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => setFaviconPreview('')}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <SafeIcon icon={FiX} className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-2 pb-3">
                        <SafeIcon icon={FiUpload} className="w-6 h-6 mb-2 text-gray-500" />
                        <p className="text-sm text-gray-500">Upload favicon</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'favicon')}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Empower Palette Section */}
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.1}}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div className="flex items-center space-x-2 mb-6">
              <SafeIcon icon={FiPalette} className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-bold text-gray-900">Empower Nutrition Palette</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Click any color to apply it to your primary, secondary, or accent colors
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {empowerPalette.map((paletteColor, index) => (
                <motion.div
                  key={index}
                  initial={{opacity: 0, scale: 0.8}}
                  animate={{opacity: 1, scale: 1}}
                  transition={{delay: index * 0.05}}
                  className="group bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                      style={{backgroundColor: paletteColor.color}}
                    />
                    <div>
                      <p className="font-medium text-gray-900">{paletteColor.name}</p>
                      <p className="text-xs text-gray-500">{paletteColor.color}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{paletteColor.description}</p>
                  <div className="grid grid-cols-3 gap-1">
                    <button
                      type="button"
                      onClick={() => setColorFromPalette('primary_color', paletteColor.color)}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                    >
                      Primary
                    </button>
                    <button
                      type="button"
                      onClick={() => setColorFromPalette('secondary_color', paletteColor.color)}
                      className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
                    >
                      Secondary
                    </button>
                    <button
                      type="button"
                      onClick={() => setColorFromPalette('accent_color', paletteColor.color)}
                      className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded hover:bg-purple-200 transition-colors"
                    >
                      Accent
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Colors Section */}
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.2}}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-6">Brand Colors</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      {...register('primary_color', {required: 'Primary color is required'})}
                      type="color"
                      className="h-12 w-16 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      {...register('primary_color')}
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
                      placeholder="#399696"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    Main brand color for buttons, links, and key elements
                  </div>
                  {errors.primary_color && (
                    <p className="mt-1 text-sm text-red-600">{errors.primary_color.message}</p>
                  )}
                </div>
              </div>

              {/* Secondary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary Color
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      {...register('secondary_color', {required: 'Secondary color is required'})}
                      type="color"
                      className="h-12 w-16 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      {...register('secondary_color')}
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
                      placeholder="#a0cfbc"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    Supporting color for backgrounds and subtle elements
                  </div>
                  {errors.secondary_color && (
                    <p className="mt-1 text-sm text-red-600">{errors.secondary_color.message}</p>
                  )}
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accent Color
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      {...register('accent_color', {required: 'Accent color is required'})}
                      type="color"
                      className="h-12 w-16 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      {...register('accent_color')}
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
                      placeholder="#ffe8ad"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    Highlight color for special elements and CTAs
                  </div>
                  {errors.accent_color && (
                    <p className="mt-1 text-sm text-red-600">{errors.accent_color.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Color Preview</h4>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{backgroundColor: watch('primary_color')}}
                  />
                  <span className="text-sm text-gray-600">Primary</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{backgroundColor: watch('secondary_color')}}
                  />
                  <span className="text-sm text-gray-600">Secondary</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{backgroundColor: watch('accent_color')}}
                  />
                  <span className="text-sm text-gray-600">Accent</span>
                </div>
              </div>
              
              {/* Sample Button Previews */}
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg text-white font-medium"
                  style={{backgroundColor: watch('primary_color')}}
                >
                  Primary Button
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg text-gray-900 font-medium border-2"
                  style={{
                    backgroundColor: watch('secondary_color'), 
                    borderColor: watch('primary_color')
                  }}
                >
                  Secondary Button
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg text-gray-900 font-medium"
                  style={{backgroundColor: watch('accent_color')}}
                >
                  Accent Button
                </button>
              </div>
            </div>
          </motion.div>

          {/* Typography Section */}
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.3}}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-6">Typography</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heading Font
                </label>
                <select
                  {...register('heading_font', {required: 'Heading font is required'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
                >
                  {fontOptions.map(font => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
                {errors.heading_font && (
                  <p className="mt-1 text-sm text-red-600">{errors.heading_font.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Body Font
                </label>
                <select
                  {...register('body_font', {required: 'Body font is required'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
                >
                  {fontOptions.map(font => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
                {errors.body_font && (
                  <p className="mt-1 text-sm text-red-600">{errors.body_font.message}</p>
                )}
              </div>
            </div>

            {/* Typography Preview */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Typography Preview</h4>
              <div className="space-y-3">
                <h1 
                  className="text-3xl font-bold text-gray-900"
                  style={{fontFamily: watch('heading_font')}}
                >
                  This is a heading
                </h1>
                <h2 
                  className="text-xl font-semibold text-gray-800"
                  style={{fontFamily: watch('heading_font')}}
                >
                  This is a subheading
                </h2>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{fontFamily: watch('body_font')}}
                >
                  This is body text that shows how your chosen font will look for paragraphs and regular content. It should be readable and comfortable for extended reading.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Footer Links Section */}
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.4}}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Footer Links</h2>
              <button
                type="button"
                onClick={addFooterLink}
                className="flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700 font-medium"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Add Link</span>
              </button>
            </div>

            <div className="space-y-4">
              <AnimatePresence>
                {footerLinks.map((link, index) => (
                  <motion.div
                    key={index}
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0, y: -20}}
                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => updateFooterLink(index, 'label', e.target.value)}
                      placeholder="Link Label"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
                    />
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => updateFooterLink(index, 'url', e.target.value)}
                      placeholder="/page-url or https://external.com"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeFooterLink(index)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>

              {footerLinks.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No footer links added yet</p>
                  <button
                    type="button"
                    onClick={addFooterLink}
                    className="mt-2 text-nutrition-600 hover:text-nutrition-700 font-medium"
                  >
                    Add your first footer link
                  </button>
                </div>
              )}
            </div>

            {/* Default Footer Links Suggestion */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Suggested Footer Links:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-blue-800">
                <div>• Privacy Policy (/privacy)</div>
                <div>• Terms of Service (/terms)</div>
                <div>• Contact (mailto:laura@empowernutrition.info)</div>
                <div>• About (/about)</div>
                <div>• Courses (/courses)</div>
                <div>• Blog (/blog)</div>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={previewChanges}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiEye} className="w-4 h-4" />
              <span>Preview Changes</span>
            </button>

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                <span>Reset</span>
              </button>

              <button
                type="submit"
                disabled={saving || uploading}
                className="flex items-center space-x-2 bg-nutrition-600 hover:bg-nutrition-700 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default BrandingSettings